import { useForm } from "@inertiajs/react";
import { useRef } from "react";
import { ReviewModal } from "@/components/ProductShowView/components/ReviewModal";
import { RoutedModal } from "@/components/RoutedModal";
import { firstError } from "@/lib/forms/first-error";

interface ReviewFormModalProps {
  /** Where the review POSTs — productReviewsPath or serviceReviewsPath. */
  path: string;
  /** Capability token from the receipt link; "" for the in-app, logged-in flow. */
  grantToken: string | null;
  /** Whether to collect a display name (guest accounts have none). */
  nameRequired: boolean;
}

/**
 * The "leave a review" modal form, shared by the product and service review pages
 * (which differ only in their POST path). Mirrors the GuestPurchaseFlow split.
 */
export function ReviewFormModal({ path, grantToken, nameRequired }: ReviewFormModalProps) {
  const form = useForm({
    score: null as number | null,
    body: "",
    name: "",
    grant_token: grantToken ?? "",
  })
    .withPrecognition("post", path)
    .setValidationTimeout(400);

  const formRef = useRef(form);
  formRef.current = form;

  return (
    <RoutedModal size="sm">
      {({ close }) => (
        <ReviewModal
          body={form.data.body}
          bodyError={form.invalid("body") ? firstError(form.errors.body) : undefined}
          close={close}
          isPending={form.processing}
          name={form.data.name}
          onBodyChange={(value) => {
            formRef.current.setData("body", value);
            formRef.current.validate("body");
          }}
          onNameChange={(value) => formRef.current.setData("name", value)}
          onScoreChange={(value) => {
            formRef.current.setData("score", value);
            formRef.current.validate("score");
          }}
          onSubmit={() => {
            formRef.current.post(path, {
              preserveState: true,
              preserveScroll: true,
              onSuccess: () => close(),
            });
          }}
          score={form.data.score}
          scoreError={form.invalid("score") ? firstError(form.errors.score) : undefined}
          showName={nameRequired}
        />
      )}
    </RoutedModal>
  );
}
